import { useEffect, useState } from "react";
import { Flame, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Blocking modal shown when the player has 0 lives. Offers Stripe checkout for
 * top-up packs so the run can begin immediately, or a graceful "wait until dawn"
 * dismissal (which just closes the modal — no lives granted).
 */
export default function LivesExhaustedModal({ open, onClose }) {
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/store/products");
        setProducts(res);
      } catch {
        toast.error("Could not load the merchant's ledger.");
      }
    })();
  }, [open]);

  const buy = async (product_key) => {
    setBusy(true);
    try {
      const res = await api.post("/store/checkout", {
        product_key,
        origin_url: window.location.origin,
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Merchant took your gold but gave no receipt.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merchant refused the trade.");
      setBusy(false);
    }
  };

  if (!open) return null;

  const packs = products?.packs || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      data-testid="lives-exhausted-modal"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }}
    >
      <div
        className="panel p-8 max-w-2xl w-full relative"
        style={{
          background: "radial-gradient(ellipse at top, rgba(90,20,10,0.4) 0%, rgba(10,5,3,0.98) 60%)",
          borderColor: "rgba(226,88,34,0.5)",
          boxShadow: "0 0 60px rgba(226,88,34,0.25)",
        }}
      >
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-[#8b7965] hover:text-[#f0e6d2] transition-colors"
            data-testid="lives-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}

        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.5em] blood-text mb-3">
            <Flame size={12} /> EMBERS EXTINGUISHED <Flame size={12} />
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-[#f0e6d2]">
            The <span className="gold-text italic">flame</span> has gone out.
          </h2>
          <p className="font-narrative text-base md:text-lg ash-text italic mt-4 max-w-md mx-auto">
            You have burned through all three of today&apos;s lives. Return at dawn (00:00 UTC)
            — or barter with the Merchant for more embers to descend right now.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {["single", "five", "ten"].map((key) => {
            const p = packs[key];
            if (!p) return null;
            const pk = `pack_${key}`;
            const price = (p.price / 100).toFixed(2);
            const bestValue = key === "five";
            return (
              <button
                key={key}
                onClick={() => buy(pk)}
                disabled={busy}
                data-testid={`lives-pack-${key}-btn`}
                className="panel p-4 text-center transition-all hover:border-[#d4af37] hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] disabled:opacity-50 disabled:cursor-wait relative overflow-hidden"
                style={{
                  background: bestValue
                    ? "linear-gradient(180deg, rgba(60,40,10,0.6), rgba(20,10,5,0.9))"
                    : "linear-gradient(180deg, rgba(20,15,8,0.5), rgba(8,5,3,0.85))",
                }}
              >
                {bestValue && (
                  <div className="absolute top-1 right-1 text-[8px] tracking-[0.3em] gold-text opacity-80">
                    BEST VALUE
                  </div>
                )}
                <div className="font-heading text-4xl gold-text">{p.lives}</div>
                <div className="text-[10px] tracking-[0.3em] ash-text mt-1">
                  {p.lives === 1 ? "EMBER" : "EMBERS"}
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 font-heading text-xl text-[#f0e6d2]">
                  ${price}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-center gap-4 text-[10px] tracking-[0.3em] ash-text">
          <span>SECURE PAYMENT · STRIPE</span>
          <span className="opacity-40">·</span>
          <span>DAILY LIVES RESET AT 00:00 UTC</span>
        </div>
      </div>
    </div>
  );
}
